import {
  CONSENT_VERSION,
  clearConsent,
  readConsent,
  writeConsent,
  type ConsentDecision,
  type ConsentStorage,
  type StoredConsent,
} from './consent.ts';

/**
 * Indirizzo dello script di Google Analytics. Sta qui e non nei componenti: è
 * l'unico host esterno che le due interfacce contattano, e deve essere
 * ritrovabile con una ricerca sola.
 */
export const GTAG_SCRIPT_ORIGIN = 'https://www.googletagmanager.com';

export function gtagScriptUrl(measurementId: string): string {
  return `${GTAG_SCRIPT_ORIGIN}/gtag/js?id=${encodeURIComponent(measurementId)}`;
}

/**
 * Ciò che serve del `window`: la coda di gtag e nulla più.
 *
 * Dichiararlo come interfaccia invece di usare `window` direttamente è quello
 * che rende questo modulo verificabile senza un browser — e insieme impedisce al
 * codice di misura di toccare qualunque altra cosa della pagina.
 */
export interface GtagTarget {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
}

/**
 * Le stesse due proprietà, dichiarate sul `window` vero.
 *
 * Senza questa dichiarazione `window` non sarebbe assegnabile a `GtagTarget`
 * (TypeScript rifiuta un oggetto che non ha *nessuna* proprietà in comune con un
 * tipo di sole proprietà opzionali), e ogni applicazione dovrebbe ripeterla per
 * conto proprio.
 */
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export type ScriptLoader = (url: string) => void;

export interface AnalyticsOptions {
  /** Vuoto = misura spenta. È il default in sviluppo e nei test. */
  readonly measurementId: string;
  readonly target: GtagTarget;
  readonly storage: ConsentStorage;
  readonly loadScript: ScriptLoader;
  readonly now?: () => Date;
}

export interface AnalyticsController {
  /** La scelta registrata, o `null` se va ancora chiesta. */
  decision(): ConsentDecision | null;
  /** Vero se il banner va mostrato: misura configurata e scelta assente. */
  shouldAsk(): boolean;
  grant(): StoredConsent;
  deny(): StoredConsent;
  /** Dimentica la scelta e torna a chiedere. */
  reset(): void;
  /** Applica la scelta già registrata. Da chiamare all'avvio dell'applicazione. */
  start(): void;
}

/**
 * Coda di gtag, creata una sola volta.
 *
 * `arguments` e non un array: `gtag.js` riconosce solo gli oggetti `arguments`
 * spinti nel `dataLayer`, e un array normale verrebbe ignorato in silenzio —
 * cioè nel modo peggiore, con la pagina che funziona e i dati che non arrivano.
 */
function ensureGtag(target: GtagTarget): (...args: unknown[]) => void {
  target.dataLayer ??= [];
  const queue = target.dataLayer;

  target.gtag ??= function gtag(): void {
    // eslint-disable-next-line prefer-rest-params -- vedi il commento sopra: gtag.js pretende `arguments`.
    queue.push(arguments);
  };

  return target.gtag;
}

/**
 * Misura d'uso con Google Analytics, subordinata al consenso.
 *
 * Due scelte che valgono più di tutto il resto del file:
 *
 * 1. **Lo script non viene caricato prima del consenso.** Non basta configurare
 *    gtag con `analytics_storage: 'denied'`: la sola richiesta a
 *    googletagmanager.com comunica a Google l'indirizzo IP del visitatore, e per
 *    il Garante è già un trattamento. Finché non c'è un «accetto», verso Google
 *    non parte nessuna richiesta.
 * 2. **Il consenso negato resta negato.** Se la persona rifiuta dopo aver
 *    accettato, lo script è già in pagina e non si può scaricare: si aggiorna il
 *    consenso a `denied`, che è ciò che gtag.js sa gestire, e non lo si carica
 *    più agli accessi successivi.
 */
export function createAnalytics(options: AnalyticsOptions): AnalyticsController {
  const { measurementId, target, storage, loadScript } = options;
  const now = options.now ?? ((): Date => new Date());
  const enabled = measurementId.trim() !== '';

  let bootstrapped = false;

  function consentPayload(analytics: ConsentDecision): Record<string, string> {
    return {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: analytics,
    };
  }

  /**
   * Accende la misura. Idempotente: chiamarla due volte non carica lo script due
   * volte né raddoppia gli eventi.
   */
  function bootstrap(): void {
    if (bootstrapped || !enabled) {
      return;
    }

    bootstrapped = true;
    const gtag = ensureGtag(target);

    // L'ordine conta: il default deve stare nella coda prima della
    // configurazione, altrimenti la prima misura parte senza vincoli.
    gtag('consent', 'default', consentPayload('denied'));
    gtag('consent', 'update', consentPayload('granted'));
    gtag('js', now());
    /**
     * `anonymize_ip` è implicito in GA4, ma dichiararlo non costa niente;
     * `allow_google_signals` spento evita che i dati finiscano nei pubblici
     * pubblicitari, che è esattamente ciò che nessuno ha acconsentito a fare.
     */
    gtag('config', measurementId, { anonymize_ip: true, allow_google_signals: false });

    loadScript(gtagScriptUrl(measurementId));
  }

  function decision(): ConsentDecision | null {
    return readConsent(storage)?.analytics ?? null;
  }

  return {
    decision,

    shouldAsk(): boolean {
      return enabled && decision() === null;
    },

    grant(): StoredConsent {
      const stored = writeConsent(storage, 'granted', { version: CONSENT_VERSION, now });
      bootstrap();
      return stored;
    },

    deny(): StoredConsent {
      const stored = writeConsent(storage, 'denied', { version: CONSENT_VERSION, now });

      if (bootstrapped) {
        ensureGtag(target)('consent', 'update', consentPayload('denied'));
      }

      return stored;
    },

    reset(): void {
      clearConsent(storage);
    },

    start(): void {
      if (decision() === 'granted') {
        bootstrap();
      }
    },
  };
}

/**
 * Carica uno script in coda al `<head>`, in modo asincrono.
 *
 * È l'unico punto del modulo che tocca il DOM, e sta qui perché entrambe le
 * interfacce lo userebbero identico.
 */
export function appendScriptLoader(documentRef: Document): ScriptLoader {
  return (url: string): void => {
    const script = documentRef.createElement('script');
    script.async = true;
    script.src = url;
    documentRef.head.appendChild(script);
  };
}
